/** A turn or a scheduled pass while it runs: what the agent is doing now, and how
 * full each agent's context window is. */
import { type ReactNode, useEffect, useRef, useState } from "react";

import type {
  ContextUsage,
  ExtractionGate,
  LiveTurn,
  PassFeed,
} from "../api/hooks/useStatefulAgents";
import { type AsyncOperationEvent, useReleaseGate } from "../api/hooks/useAsyncOperation";
import { formatTokens } from "../utils/formatTokens";
import { ContextBreakdown } from "./ContextBreakdown";
import { useBreakdownToggle } from "./useBreakdownToggle";
import { agentDisplayLabel, agentsLabel } from "../utils/runParticipants";
import styles from "./RunView.module.css";
import { Chevron } from "./Chevron";

// The RUNNING turn's whole trace, live - uncapped, since "what has it been doing?"
// is asked while it runs. Open by default; `override` makes a fold stick for as
// long as the block is mounted.
const LiveTurnTrace = ({
  events,
  title,
  fallbackAgent,
  tail,
}: {
  events: readonly AsyncOperationEvent[];
  title?: string;
  /** The turn's current wait, as the feed's last line, where the operator is reading. */
  tail?: ReactNode;
  // Whose the untagged steps are - the agent driving this turn. A delegated step
  // carries its own ``groupLabel``; everything else is this one's own work.
  fallbackAgent?: string;
}) => {
  // Named for whoever is actually working. It used to say "the manager" whatever
  // ran, so a conversation with Grid narrated itself as somebody else.
  const heading = title ?? `What ${fallbackAgent ?? "the agent"} is doing`;
  const [override, setOverride] = useState<boolean | null>(null);
  const open = override ?? true;
  const feedRef = useRef<HTMLUListElement | null>(null);
  const hasTail = Boolean(tail);
  // Follow the tail only while the operator is AT it, so they can read history.
  const [following, setFollowing] = useState(true);
  useEffect(() => {
    const el = feedRef.current;
    if (el && following) el.scrollTop = el.scrollHeight;
  }, [events.length, following, open, hasTail]);
  if (events.length === 0) {
    return null;
  }
  return (
    <div className={styles.trace}>
      <TraceHeader
        open={open}
        onToggle={() => setOverride(!open)}
        title={heading}
        count={`${events.length} step${events.length === 1 ? "" : "s"}`}
      />
      {!open && tail}
      {open && (
        <ul
          className={styles.eventFeed}
          ref={feedRef}
          onScroll={(e) => {
            const el = e.currentTarget;
            // Slack for sub-pixel rounding and a row arriving mid-scroll.
            setFollowing(el.scrollHeight - el.scrollTop - el.clientHeight < 24);
          }}
        >
          {events.map((ev, i) => {
            // Tagged on EVERY line, thinking included, and on every run rather
            // than only the ones that delegated: with specialists running at once an
            // untagged "I'll start by seeing what is outstanding" reads as the
            // manager saying it, and on a solo run the operator could not tell WHICH
            // agent they were watching - which is worse than the noise the tag adds.
            // Through the display-label helper even though the server sends a
            // label: a step emitted by an older running build sends the raw key
            // ("design_readiness"), and the helper is a no-op on a label that is
            // already one.
            const who = ev.groupLabel ? agentDisplayLabel(ev.groupLabel) : fallbackAgent;
            return (
              <li
                key={`${ev.at}-${i}`}
                className={ev.kind === "thinking" ? styles.eventThinking : styles.eventTool}
              >
                {/* The trailing space is inside the span, not only CSS margin, so
                    a feed copied out of the page does not read "[Manager]I'll". */}
                {who && <span className={styles.eventAgent}>{`[${who}] `}</span>}
                {ev.kind === "thinking" ? ev.text : `→ ${ev.text}`}
              </li>
            );
          })}
          {tail && <li className={styles.eventTool}>{tail}</li>}
        </ul>
      )}
      {open && !following && (
        <button type="button" className={styles.followTail} onClick={() => setFollowing(true)}>
          ↓ Jump to latest
        </button>
      )}
    </div>
  );
};



const TraceHeader = ({
  open,
  onToggle,
  title,
  count,
}: {
  open: boolean;
  onToggle: () => void;
  title: string;
  count: string;
}) => (
  <button type="button" className={styles.traceHeader} aria-expanded={open} onClick={onToggle}>
    <span className={styles.changeCaret} aria-hidden="true">
      <Chevron tree open={open} />
    </span>
    <span className={styles.traceTitle}>{title}</span>
    {count && <span className={styles.traceCount}>{count}</span>}
  </button>
);


// Each agent's context window: fills as it reads, drops when it compacts.
//
// One line per window, foldable to one line in total. A turn can involve several
// agents, and on a wide run that is a stack tall enough to push the live feed
// (the thing an operator is actually watching) off the screen - but it is worth
// seeing by default, because a window filling up is what makes a turn wrap up
// early.
//
// Folded, it shows EVERY window as "Name: pct", not the fullest one and a count.
// With specialists running concurrently, how many windows are open at once is
// the thing the gauge is being read for, and "+3 more" hid exactly that.

// The gauge is keyed by the agent's DISPLAY label (``agent_label`` server-side),
// so this matches "Manager" and "Atlas (manager)" alike. The manager sorts first
// because it is the one an operator looks for; the specialists follow by name,
// so a row does not jump as its usage changes.
const isManagerLabel = (label: string): boolean => /manager/i.test(label);

const contextPct = (usage: { used: number; budget: number }): number =>
  Math.min(100, Math.round((100 * usage.used) / usage.budget));

const ContextGauge = ({ context }: { context?: ContextUsage }) => {
  const [open, setOpen] = useState(true);
  const agents = Object.entries(context ?? {})
    .filter(([, usage]) => usage.budget > 0)
    .sort(([a], [b]) => {
      if (isManagerLabel(a) !== isManagerLabel(b)) return isManagerLabel(a) ? -1 : 1;
      return agentDisplayLabel(a).localeCompare(agentDisplayLabel(b));
    });
  if (agents.length === 0) return null;
  const toggle = agents.length > 1 && (
    <button
      type="button"
      className={styles.contextToggle}
      aria-expanded={open}
      onClick={() => setOpen(!open)}
      title={open ? "Show every window on one line" : "Show every window in full"}
    >
      <Chevron tree open={open} />
    </button>
  );
  if (!open) {
    return (
      <div className={styles.contextGauge}>
        <div className={styles.contextLine}>
          {toggle}
          <span className={styles.contextSummary}>
            {agents
              .map(([agent, usage]) => `${agentDisplayLabel(agent)}: ${contextPct(usage)}%`)
              .join(" | ")}
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.contextGauge}>
      {agents.map(([agent, usage], i) => (
        <ContextGaugeLine
          key={agent}
          agent={agent}
          usage={usage}
          // The toggle rides the first line rather than taking one of its own.
          lead={
            i === 0 && agents.length > 1 ? (
              toggle
            ) : (
              <span className={styles.contextToggleSpacer} aria-hidden />
            )
          }
        />
      ))}
    </div>
  );
};

const ContextGaugeLine = ({
  agent,
  usage,
  lead,
}: {
  agent: string;
  usage: ContextUsage[string];
  lead: ReactNode;
}) => {
  const { open, button } = useBreakdownToggle();
  const pct = contextPct(usage);
  return (
    <>
      <div className={styles.contextLine}>
        {lead}
        <span className={styles.contextName}>{agentDisplayLabel(agent)}</span>
        <span className={styles.contextTrack} aria-hidden>
          <span className={styles.contextFill} style={{ width: `${pct}%` }} />
        </span>
        <span className={styles.contextCount}>
          {formatTokens(usage.used)} / {formatTokens(usage.budget)} ({pct}%)
        </span>
        {usage.composition && button}
      </div>
      {open && usage.composition && (
        <ContextBreakdown used={usage.used} composition={usage.composition} />
      )}
    </>
  );
};

// The gate's own words for an upload still blocking it (`extraction_gate` statuses).
const GATE_STATUS_LABEL: Record<string, string> = {
  pending: "waiting to be prepared",
  running: "being read",
  extracting: "being read",
};

const useSecondsUntil = (iso: string | null): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return iso ? Math.max(0, Math.round((Date.parse(iso) - now) / 1000)) : 0;
};

// One release skips whatever is left of the turn's preparation, sync and documents alike.
// Its state lives in the panel: the card moves between the header, the feed's tail and
// the folded feed, and a remount must not offer a second release.
interface Release {
  onRelease: () => void;
  disabled: boolean;
  released: boolean;
  error: string | null;
}

const ReleaseButton = ({ release, label }: { release: Release; label: string }) => (
  <>
    <button
      type="button"
      className={styles.secondary}
      disabled={release.disabled}
      onClick={release.onRelease}
    >
      {release.released ? "Continuing…" : label}
    </button>
    {release.error && <p className={styles.error}>Could not skip: {release.error}</p>}
  </>
);

const GateCard = ({ gate, release }: { gate: ExtractionGate; release: Release }) => {
  const left = useSecondsUntil(gate.deadline_at);
  const n = gate.uploads.length;
  if (gate.phase === "sync") {
    return <ReleaseButton release={release} label="Skip sync" />;
  }
  if (n === 0) {
    // The sync just ended; the document list arrives with the wait's first poll.
    return null;
  }
  return (
    <div className={styles.gateCard}>
      <span>
        Waiting for {n} document{n === 1 ? "" : "s"} - continues without{" "}
        {n === 1 ? "it" : "them"} in {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}
      </span>
      <ul className={styles.gateDocs}>
        {gate.uploads.map((u) => (
          <li key={u.upload_id}>
            <strong>{u.name}</strong> - {GATE_STATUS_LABEL[u.status] ?? u.status}
          </li>
        ))}
      </ul>
      <ReleaseButton release={release} label="Continue without waiting" />
    </div>
  );
};

// What the agent is doing right now: enough to tell a thinking agent from a wedged
// one (principle 14), riding the run poll. Shared with the agent's run page;
// `label` is the only difference (a project vs a list of sites).
export const LiveTurnPanel = ({
  turn,
  label,
  stopping,
  onStop,
  fallbackAgent,
}: {
  turn: LiveTurn;
  label: string;
  stopping: boolean;
  onStop: () => void;
  /** Whose the turn's own untagged steps are, when it delegates. */
  fallbackAgent?: string;
}) => {
  const releaseGate = useReleaseGate();
  const released = releaseGate.data?.released === true;
  const release: Release = {
    onRelease: () => releaseGate.mutate({ operation_key: turn.operation_key }),
    disabled: stopping || released || releaseGate.isLoading,
    released,
    error: releaseGate.isError ? releaseGate.error.message : null,
  };
  const gateCard = turn.gate && <GateCard gate={turn.gate} release={release} />;
  return (
    <div className={styles.working}>
      <div className={styles.workingCard}>
        {/* Two columns, unequal: the windows take the room, the buttons what they need. */}
        <div className={styles.workingHead}>
          <div className={styles.workingStatus}>
            {turn.context && Object.keys(turn.context).length > 0 ? (
              <ContextGauge context={turn.context} />
            ) : (
              <span>{label}</span>
            )}
          </div>
          <div className={styles.workingActions}>
            <button type="button" className={styles.secondary} disabled={stopping} onClick={onStop}>
              {stopping ? "Stopping…" : "Stop"}
            </button>
          </div>
        </div>
        {turn.events.length === 0 && gateCard}
      </div>
      {turn.events.length > 0 && (
        <div className={styles.workingCard}>
          {/* Keyed on the turn so a new turn doesn't inherit the previous fold. */}
          <LiveTurnTrace
            key={turn.operation_key}
            events={turn.events}
            fallbackAgent={fallbackAgent}
            tail={gateCard}
          />
        </div>
      )}
    </div>
  );
};

// A scheduled pass's current turn, from the pass's own feed. Its Stop is the run
// page's "Stop run", which stops the whole pass rather than one lane's turn.
export const LivePassPanel = ({ feed }: { feed: PassFeed }) => {
  if (feed.stage === "preparing") {
    return (
      <div className={styles.working}>
        <div className={styles.workingCard}>
          <span>
            Checking Gmail, Drive and Slack for anything new before the agents
            start.
          </span>
        </div>
        {feed.events.length > 0 && (
          <div className={styles.workingCard}>
            <LiveTurnTrace
              key="preparing"
              events={feed.events}
              title="Getting the latest email and files"
            />
          </div>
        )}
      </div>
    );
  }
  const who = agentsLabel(feed.agent);
  const where = feed.project_name || feed.project_id;
  // Several specialists work the site at once, so naming only the agent whose
  // turn owns the feed (the manager) described a fraction of what is on screen.
  // Read off the feed rather than the roster: a pass with nobody to delegate to
  // is still one agent, and should still say so.
  const team = feed.events.some((ev) => ev.groupLabel);
  const actors = team ? "the agents" : who;
  const verb = team ? "are" : "is";
  return (
    <div className={styles.working}>
      <div className={styles.workingCard}>
        <span>
          {`${team ? "The agents" : who} ${verb} working on ${where}`}
          {" - each site appears above as it is finished."}
        </span>
        <ContextGauge context={feed.context} />
      </div>
      {feed.events.length > 0 && (
        <div className={styles.workingCard}>
          {/* Keyed on the turn so the next site starts with its feed open. */}
          <LiveTurnTrace
            key={`${feed.project_id}:${feed.agent}`}
            events={feed.events}
            title={`What ${actors} ${verb} doing on ${where}`}
            fallbackAgent={who}
          />
        </div>
      )}
    </div>
  );
};

